import { useCallback, useEffect, useRef, useState } from "react"
import toast from "react-hot-toast"
import {
  listStudentBookmarks,
  unbookmarkStudentQuestion,
} from "../../../../api/studentQuestions"
import { getLearnerApiErrorMessage } from "../../../../auth/learner/errors"
import type { PaginationMeta, StudentQuestionListItem } from "../../Questions/types"
import { BOOKMARKS_PAGE_SIZE } from "../types"
import { mapBookmarkCard } from "../utils/mapBookmarkCard"

const useBookmarks = () => {
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<StudentQuestionListItem[]>([])
  const [pagination, setPagination] = useState<PaginationMeta | null>(null)
  const [loading, setLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState("")
  const [removingId, setRemovingId] = useState<string | null>(null)
  const requestId = useRef(0)
  const removingRef = useRef(false)

  const refetch = useCallback(async (pageNumber: number) => {
    const id = ++requestId.current
    setLoading(true)
    setError("")
    try {
      const res = await listStudentBookmarks(pageNumber, BOOKMARKS_PAGE_SIZE)
      if (id !== requestId.current) return
      if (!res.data?.success) {
        throw new Error(res.data?.message || "Failed to load bookmarks")
      }
      setItems(Array.isArray(res.data.data) ? res.data.data : [])
      setPagination(res.data.pagination ?? null)
    } catch (err) {
      if (id !== requestId.current) return
      setItems([])
      setPagination(null)
      setError(getLearnerApiErrorMessage(err, "Failed to load bookmarks"))
    } finally {
      if (id === requestId.current) {
        setLoading(false)
        setHasLoaded(true)
      }
    }
  }, [])

  useEffect(() => {
    refetch(page)
  }, [page, refetch])

  const dropLocal = useCallback(
    async (questionId: string) => {
      const remaining = items.filter((item) => item.id !== questionId)
      setItems(remaining)
      setPagination((prev) =>
        prev ? { ...prev, total: Math.max(0, prev.total - 1) } : prev
      )
      const nextPage = remaining.length === 0 && page > 1 ? page - 1 : page
      if (nextPage !== page) setPage(nextPage)
      else await refetch(page)
    },
    [items, page, refetch]
  )

  const removeBookmark = useCallback(
    async (questionId: string): Promise<boolean> => {
      if (removingRef.current) return false
      removingRef.current = true
      const snapshot = items
      const snapshotPagination = pagination
      setRemovingId(questionId)
      setItems((prev) => prev.filter((item) => item.id !== questionId))
      setPagination((prev) =>
        prev ? { ...prev, total: Math.max(0, prev.total - 1) } : prev
      )
      try {
        await unbookmarkStudentQuestion(questionId)
        toast.success("Bookmark removed")
        const nextPage = snapshot.length <= 1 && page > 1 ? page - 1 : page
        if (nextPage !== page) setPage(nextPage)
        else await refetch(page)
        return true
      } catch (err) {
        setItems(snapshot)
        setPagination(snapshotPagination)
        toast.error(getLearnerApiErrorMessage(err, "Could not update bookmark"))
        return false
      } finally {
        removingRef.current = false
        setRemovingId(null)
      }
    },
    [items, page, pagination, refetch]
  )

  return {
    cards: items.map(mapBookmarkCard),
    pagination,
    loading,
    hasLoaded,
    error,
    page,
    setPage,
    removeBookmark,
    dropLocal,
    removingId,
  }
}

export default useBookmarks
